import React from 'react';
import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import {
  useAsyncAutocomplete,
  UseAsyncAutocompleteOptions,
} from './useAsyncAutocomplete';

type ManagedAutocompleteKeys =
  | 'options'
  | 'loading'
  | 'filterOptions'
  | 'onInputChange'
  | 'inputValue'
  | 'onOpen'
  | 'value'
  | 'onChange'
  | 'getOptionLabel'
  | 'isOptionEqualToValue'
  | 'renderInput'
  | 'noOptionsText';

export type AsyncSearchAutocompleteProps<T> = UseAsyncAutocompleteOptions<T> & {
  onChange: (event: React.SyntheticEvent, value: T | null) => void;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  textFieldProps?: TextFieldProps;
  noOptionsTextWhenEmpty?: string;
  noOptionsTextWhenSearching?: string;
  renderInput?: AutocompleteProps<T, false, false, false>['renderInput'];
} & Partial<Omit<AutocompleteProps<T, false, false, false>, ManagedAutocompleteKeys>>;

function AsyncSearchAutocomplete<T>({
  fetchOptions,
  enabled = true,
  value,
  getOptionLabel,
  getOptionId,
  debounceMs,
  refreshOnOpen,
  onChange,
  isOptionEqualToValue,
  textFieldProps,
  noOptionsTextWhenEmpty = 'Nhập để tìm kiếm',
  noOptionsTextWhenSearching = 'Không tìm thấy kết quả',
  renderInput,
  ...autocompleteRest
}: AsyncSearchAutocompleteProps<T>) {
  const { autocompleteProps, inputValue } = useAsyncAutocomplete({
    fetchOptions,
    enabled,
    value,
    getOptionLabel,
    getOptionId,
    debounceMs,
    refreshOnOpen,
  });

  const noOptionsText = inputValue.trim()
    ? noOptionsTextWhenSearching
    : noOptionsTextWhenEmpty;

  return (
    <Autocomplete
      {...autocompleteRest}
      {...autocompleteProps}
      value={value}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText={noOptionsText}
      renderInput={
        renderInput ??
        ((params) => (
          <TextField {...params} {...textFieldProps} size={textFieldProps?.size ?? 'small'} />
        ))
      }
    />
  );
}

export default AsyncSearchAutocomplete;
